import React from 'react';
import { FileText, X, Check } from 'lucide-react';
import { usersAPI } from '../../../services/api';
import { toast } from 'sonner';

const ReviewRegistrationModal = ({ 
  isOpen, 
  onClose, 
  selectedUser, 
  getUserDisplayName,
  getDocumentDisplayName,
  onApprove,
  onReject
}) => {
  if (!isOpen || !selectedUser) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const roleNames = {
      issuer: 'Issuer',
      facility_agent: 'Facility Agent',
      custodian: 'Custodian',
      super_admin: 'Super Admin',
      investor: 'Investor',
      exchange: 'Exchange',
      arranger: 'Arranger'
    };
    return roleNames[role] || role;
  };

  const getRegistrationStatusBadge = (registrationStatus) => {
    const statusConfig = {
      verified: { label: 'Verified', class: 'badge-success' },
      pending_verification: { label: 'Pending Verification', class: 'badge-warning' },
      rejected: { label: 'Rejected', class: 'badge-error' }
    };
    const config = statusConfig[registrationStatus] || { label: registrationStatus, class: 'badge-secondary' };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const handleDownloadDocument = async (docType, fileName) => {
    try {
      const response = await usersAPI.downloadDocument(selectedUser._id, docType);
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `${docType}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handleApprove = () => {
    onApprove();
    onClose();
  };

  const handleReject = () => {
    onReject();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FileText className="h-6 w-6 text-primary-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Registration Details - {getUserDisplayName(selectedUser)}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-900 border-b pb-2">Basic Information</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-sm text-gray-900">{selectedUser.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Role</label>
                  <p className="text-sm text-gray-900">{getRoleBadge(selectedUser.role)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Account Type</label>
                  <p className="text-sm text-gray-900">{selectedUser.accountType || 'Not specified'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Registration Status</label>
                  <div>{getRegistrationStatusBadge(selectedUser.registrationStatus)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Registered On</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedUser.createdAt)}</p>
                </div>
              </div>
            </div>

            {/* Personal/Corporate Information */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-900 border-b pb-2">
                {selectedUser.accountType === 'corporate' ? 'Corporate Information' : 'Personal Information'}
              </h4>
              <div className="space-y-3">
                {selectedUser.personalInfo && (
                  <>
                    {selectedUser.personalInfo.fullName && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Full Name</label>
                        <p className="text-sm text-gray-900">{selectedUser.personalInfo.fullName}</p>
                      </div>
                    )}
                    {selectedUser.personalInfo.firstName && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Name</label>
                        <p className="text-sm text-gray-900">{selectedUser.personalInfo.firstName} {selectedUser.personalInfo.lastName}</p>
                      </div>
                    )}
                    {selectedUser.personalInfo.nricPassport && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">NRIC/Passport</label>
                        <p className="text-sm text-gray-900">{selectedUser.personalInfo.nricPassport}</p>
                      </div>
                    )}
                    {selectedUser.personalInfo.dateOfBirth && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                        <p className="text-sm text-gray-900">{new Date(selectedUser.personalInfo.dateOfBirth).toLocaleDateString()}</p>
                      </div>
                    )}
                    {selectedUser.personalInfo.nationality && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Nationality</label>
                        <p className="text-sm text-gray-900">{selectedUser.personalInfo.nationality}</p>
                      </div>
                    )}
                    {selectedUser.personalInfo.phoneNumber && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Phone Number</label>
                        <p className="text-sm text-gray-900">{selectedUser.personalInfo.phoneNumber}</p>
                      </div>
                    )}
                    {selectedUser.personalInfo.address && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Address</label>
                        <p className="text-sm text-gray-900">{selectedUser.personalInfo.address}</p>
                      </div>
                    )}
                    {selectedUser.personalInfo.occupation && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Occupation</label>
                        <p className="text-sm text-gray-900">{selectedUser.personalInfo.occupation}</p>
                      </div>
                    )}
                    {selectedUser.personalInfo.annualIncome && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Annual Income</label>
                        <p className="text-sm text-gray-900">{selectedUser.personalInfo.annualIncome}</p>
                      </div>
                    )}
                  </>
                )}
                
                {selectedUser.corporateInfo && (
                  <>
                    {selectedUser.corporateInfo.legalEntityName && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Legal Entity Name</label>
                        <p className="text-sm text-gray-900">{selectedUser.corporateInfo.legalEntityName}</p>
                      </div>
                    )}
                    {selectedUser.corporateInfo.companyName && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Company Name</label>
                        <p className="text-sm text-gray-900">{selectedUser.corporateInfo.companyName}</p>
                      </div>
                    )}
                    {selectedUser.corporateInfo.businessRegistrationNumber && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Registration Number</label>
                        <p className="text-sm text-gray-900">{selectedUser.corporateInfo.businessRegistrationNumber}</p>
                      </div>
                    )}
                    {selectedUser.corporateInfo.registrationType && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Registration Type</label>
                        <p className="text-sm text-gray-900">{selectedUser.corporateInfo.registrationType}</p>
                      </div>
                    )}
                    {selectedUser.corporateInfo.dateOfIncorporation && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Date of Incorporation</label>
                        <p className="text-sm text-gray-900">{new Date(selectedUser.corporateInfo.dateOfIncorporation).toLocaleDateString()}</p>
                      </div>
                    )}
                    {selectedUser.corporateInfo.registeredAddress && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Registered Address</label>
                        <p className="text-sm text-gray-900">{selectedUser.corporateInfo.registeredAddress}</p>
                      </div>
                    )}
                    {selectedUser.corporateInfo.operatingAddress && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Operating Address</label>
                        <p className="text-sm text-gray-900">{selectedUser.corporateInfo.operatingAddress}</p>
                      </div>
                    )}
                    {selectedUser.corporateInfo.phoneNumber && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Phone Number</label>
                        <p className="text-sm text-gray-900">{selectedUser.corporateInfo.phoneNumber}</p>
                      </div>
                    )}
                    {selectedUser.corporateInfo.website && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Website</label>
                        <p className="text-sm text-gray-900">{selectedUser.corporateInfo.website}</p>
                      </div>
                    )}
                    {selectedUser.corporateInfo.businessNature && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Business Nature</label>
                        <p className="text-sm text-gray-900">{selectedUser.corporateInfo.businessNature}</p>
                      </div>
                    )}
                    {selectedUser.corporateInfo.annualRevenue && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Annual Revenue</label>
                        <p className="text-sm text-gray-900">{selectedUser.corporateInfo.annualRevenue}</p>
                      </div>
                    )}
                    {selectedUser.corporateInfo.numberOfEmployees && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Number of Employees</label>
                        <p className="text-sm text-gray-900">{selectedUser.corporateInfo.numberOfEmployees}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Documents Section */}
          {selectedUser.documents && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Uploaded Documents</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(selectedUser.documents).map(([docType, fileName]) => {
                  if (!fileName) return null;

                  return (
                    <div key={docType} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col items-center text-center space-y-3">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                          <FileText className="h-6 w-6 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {getDocumentDisplayName(docType)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">PDF Document</p>
                        </div>
                        <button 
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 hover:border-primary-300 transition-colors"
                          onClick={() => handleDownloadDocument(docType, fileName)}
                        >
                          <FileText className="h-3 w-3 mr-1" />
                          Download
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {Object.keys(selectedUser.documents).filter(key => selectedUser.documents[key]).length === 0 && (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No documents uploaded yet</p>
                </div>
              )}
            </div>
          )}

          {/* Authorized Representatives */}
          {selectedUser.representatives && selectedUser.representatives.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Authorized Representatives</h4>
              <div className="space-y-4">
                {selectedUser.representatives.map((rep, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Full Name</label>
                        <p className="text-sm text-gray-900">{rep.fullName}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Position</label>
                        <p className="text-sm text-gray-900">{rep.position}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Email</label>
                        <p className="text-sm text-gray-900">{rep.email}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Phone</label>
                        <p className="text-sm text-gray-900">{rep.phoneNumber}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
          <div className="flex space-x-3">
            <button
              onClick={handleReject}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
            >
              <X className="h-4 w-4 mr-2" />
              Reject
            </button>
            <button
              onClick={handleApprove}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
            >
              <Check className="h-4 w-4 mr-2" />
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewRegistrationModal;